/* eslint-disable @typescript-eslint/no-unused-vars */

import React from 'react';
import { Card, Title, Text, Skeleton, Box, Group, Paper, Tooltip as MantineTooltip, Progress } from '@mantine/core';
import { IconChartBar, IconInfoCircle, IconAlertCircle, IconCircleCheck } from '@tabler/icons-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Scatter,
  ScatterChart,
  RadialBarChart,
  RadialBar,
  Label,
} from 'recharts';
import { ChartConfig, DataBoxConfig, ReportCardConfig } from '../../types/dashboard';

interface DynamicChartProps {
  config: ChartConfig;
  data: any[];
  isLoading?: boolean;
}

// Define a set of default colors for charts
const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe', '#00C49F', '#FFBB28', '#FF8042'];

// Custom formatters
const formatCurrencyTooltip = (value: any, name: any) => {
  // For dollar values, format with commas and $ sign
  return [`$${value.toLocaleString()}`, name];
};

const formatPercentTooltip = (value: any, name: any) => {
  // For percentage values, format with % sign
  return [`${value.toLocaleString()}%`, name];
};

const formatPercent = (n: number, decimalPlaces: number = 2): string => {
  // Assumes 'n' is a decimal (e.g., 0.5 for 50%)
  const percentageValue = (n * 100).toFixed(decimalPlaces);
  return `${percentageValue}%`;
};

const formatString = (value: any, chars: string, prepend = false): string => {
  return prepend ? `${chars}${value}%` : `${value}${chars}`;
};

// Format quarter with total count below
// currently causing circular json while rendering
// const formatQuarterWithTotal = (value: string, data: any[]) => {
//   // Find the data point for this quarter
//   const quarterData = data.find((item) => item.quarter === value);
//   console.log('quarter', quarterData);
//   if (quarterData && quarterData.total) {
//     return (
//       <tspan>
//         <tspan x="0" dy="0">
//           {value}
//         </tspan>
//         <tspan x="0" dy="1.2em" fontSize="10">{`(${quarterData.total} total)`}</tspan>
//       </tspan>
//     );
//   }
//   return value;
// };

// Component for rendering the supplementary data box
// const DataBox = ({ dataBox }: { dataBox: ChartConfig['dataBox'] }) => {
//   if (!dataBox) return null;

//   return (
//     <Paper
//       p="md"
//       radius="md"
//       shadow="xs"
//       sx={(theme) => ({
//         backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
//         border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
//         minWidth: 150,
//         maxWidth: 200,
//         display: 'flex',
//         flexDirection: 'column',
//         alignItems: 'center',
//         justifyContent: 'center',
//       })}
//     >
//       <Group position="center" spacing="xs">
//         <Text size="sm" color="dimmed" weight={600} transform="uppercase">
//           {dataBox.label}
//         </Text>
//         {dataBox.tooltip && (
//           <MantineTooltip label={dataBox.tooltip} position="top" multiline width={220}>
//             <IconInfoCircle size={16} style={{ opacity: 0.5 }} />
//           </MantineTooltip>
//         )}
//       </Group>
//       <Text size="xl" weight={700} color={dataBox.color || 'inherit'} mt={4} align="center">
//         {dataBox.value}
//       </Text>
//       {dataBox.descriptor && (
//         <Text size="xs" color="dimmed" mt={2} align="center" style={{ fontStyle: 'italic' }}>
//           {dataBox.descriptor}
//         </Text>
//       )}
//     </Paper>
//   );
// };

// Component to render the threshold chart
const ThresholdChart = ({ value, config }: { value: number; config: ChartConfig['threshold'] }) => {
  if (!config) return null;

  const threshold = config.threshold;
  const isAboveThreshold = value > threshold;
  const belowThresholdColor = config.belowThresholdColor || '#4CAF50'; // Green by default
  const aboveThresholdColor = config.aboveThresholdColor || '#e8e8e8'; // Gray by default
  const thresholdLineColor = config.thresholdLineColor || '#FF5722'; // Red by default

  return (
    <Box
      sx={(theme) => ({
        padding: theme.spacing.xl,
        position: 'relative',
        borderRadius: theme.radius.md,
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
        border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
      })}
    >
      <Group position="apart" mb="md">
        <Text weight={700} size="lg">
          {value.toFixed(1)}%
        </Text>
        <Group spacing="xs">
          {isAboveThreshold ? <IconAlertCircle size={20} color={aboveThresholdColor} /> : <IconCircleCheck size={20} color={belowThresholdColor} />}
          <Text color={isAboveThreshold ? aboveThresholdColor : belowThresholdColor}>{isAboveThreshold ? 'Threshold Exceeded' : 'Within Threshold'}</Text>
        </Group>
      </Group>

      <Box sx={{ position: 'relative', height: 40 }}>
        <Progress value={value} color={isAboveThreshold ? aboveThresholdColor : belowThresholdColor} size="xl" radius="md" sx={{ height: '100%' }} />

        {/* Threshold line */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${threshold}%`,
            width: 3,
            backgroundColor: thresholdLineColor,
            zIndex: 2,
            boxShadow: '0 0 5px rgba(0,0,0,0.3)',
          }}
        />
      </Box>

      {config.annotation && (
        <Text size="sm" color="dimmed" mt="md">
          <strong>Note:</strong> {config.annotation}
        </Text>
      )}
    </Box>
  );
};

export const DynamicChart: React.FC<DynamicChartProps> = ({ config, data, isLoading }) => {
  if (isLoading) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Skeleton height={20} width="70%" mb="md" />
        <Skeleton height={config.height || 300} radius="md" />
      </Card>
    );
  }

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Title order={4}>{config.title}</Title>
        {config.description && (
          <Text size="sm" color="dimmed">
            {config.description}
          </Text>
        )}
        <Text size="sm" color="dimmed" mt="lg">
          No data available
        </Text>
      </Card>
    );
  }

  // Helper function to render the correct chart type based on the configuration
  const renderChart = () => {
    // console.log('config', config);
    switch (config.type) {
      case 'threshold':
        // For threshold chart, we expect a single value in the first item of the data array
        // Either directly as a number, or as a percentage property
        if (!data || data.length === 0) return <Text>No data available</Text>;

        const value = typeof data[0] === 'number' ? data[0] : data[0].value !== undefined ? data[0].value : data[0][config.series[0].dataKey];

        return <ThresholdChart value={typeof value === 'number' ? value : parseFloat(value)} config={config.threshold} />;

      case 'line':
        return (
          <LineChart data={data}>
            {config.grid !== false && <CartesianGrid strokeDasharray="3 3" />}

            {config.xAxis && <XAxis dataKey={config.xAxis.dataKey} type={(config.xAxis.type === 'auto' ? 'category' : config.xAxis.type) || 'category'} hide={config.xAxis.hide} />}

            {config.yAxis &&
              (Array.isArray(config.yAxis) ? (
                config.yAxis.map((axis, index) => (
                  <YAxis
                    key={index}
                    yAxisId={index}
                    dataKey={axis.dataKey}
                    label={axis.label ? { value: axis.label, angle: -90, position: 'insideLeft' } : undefined}
                    domain={axis.domain || ['auto', 'auto']}
                    orientation={index === 0 ? 'left' : 'right'}
                    hide={axis.hide}
                  />
                ))
              ) : (
                <YAxis
                  dataKey={config.yAxis.dataKey}
                  label={config.yAxis.label ? { value: config.yAxis.label, angle: -90, position: 'insideLeft' } : undefined}
                  domain={config.yAxis.domain || ['auto', 'auto']}
                  hide={config.yAxis.hide}
                />
              ))}

            <Tooltip
              cursor={false}
              formatter={(value, name, props) => {
                // Use custom formatter if specified in config
                if (config.tooltip?.formatter === 'formatCurrencyTooltip') {
                  return formatCurrencyTooltip(value, name);
                }

                if (config.tooltip?.formatter === 'formatPercentTooltip') {
                  return formatPercentTooltip(value, name);
                }

                return [value, name];
              }}
              contentStyle={{
                fontSize: '12px',
                backgroundColor: '#333', // Dark background
                color: '#fff', // Light text
                border: 'none',
                borderRadius: '4px',
              }}
              itemStyle={{ padding: '2px 0' }}
              separator=": "
            />

            {config.legend && <Legend verticalAlign={config.legend.verticalAlign || 'bottom'} align={config.legend.align || 'center'} layout={config.legend.layout || 'horizontal'} />}

            {config.series.map((series, index) => (
              <Line
                key={series.dataKey}
                type="monotone"
                dataKey={series.dataKey}
                name={series.name}
                stroke={series.stroke || config.colors?.[index % (config.colors?.length || 1)] || COLORS[index % COLORS.length]}
                dot={series.dot}
                activeDot={series.activeDot}
                isAnimationActive={series.isAnimationActive !== false}
              />
            ))}
          </LineChart>
        );

      case 'multiline':
        return (
          <LineChart data={data}>
            {config.grid !== false && <CartesianGrid strokeDasharray="3 3" />}

            {config.xAxis && (
              <XAxis
                dataKey={config.xAxis.dataKey}
                type={(config.xAxis.type === 'auto' ? 'category' : config.xAxis.type) || 'category'}
                hide={config.xAxis.hide}
                tick={{ fontSize: 12 }}
                tickMargin={config.xAxis.tickFormatter === 'formatQuarterWithTotal' ? 20 : 8} // Increase margin for two-line ticks
                height={config.xAxis.tickFormatter === 'formatQuarterWithTotal' ? 60 : 30} // Increase height for two-line ticks
              />
            )}

            {config.yAxis &&
              (Array.isArray(config.yAxis) ? (
                config.yAxis.map((axis, index) => (
                  <YAxis
                    key={index}
                    yAxisId={index}
                    dataKey={axis.dataKey}
                    label={axis.label ? { value: axis.label, angle: -90, position: 'insideLeft' } : undefined}
                    domain={axis.domain || ['auto', 'auto']}
                    orientation={index === 0 ? 'left' : 'right'}
                    hide={axis.hide}
                    tickCount={axis.tickCount}
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => (config.yAxis?.tickFormatter === 'formatPercent' ? formatPercent(value) : value)}
                  />
                ))
              ) : (
                <YAxis
                  dataKey={config.yAxis.dataKey}
                  label={config.yAxis.label ? { value: config.yAxis.label, angle: -90, position: 'insideLeft' } : undefined}
                  domain={config.yAxis.domain || ['auto', 'auto']}
                  hide={config.yAxis.hide}
                  tickCount={config.yAxis.tickCount}
                  tick={{ fontSize: 12 }}
                />
              ))}

            <Tooltip
              shared={true}
              formatter={(value, name) => [value, name]}
              contentStyle={{
                fontSize: '12px',
                // backgroundColor: 'rgba(0, 0, 0, 0.4)',
                backgroundColor: '#333', // Dark background
                color: '#fff', // Light text
                border: 'none',
                borderRadius: '4px',
              }}
            />

            {config.legend && (
              <Legend
                verticalAlign={config.legend.verticalAlign || 'bottom'}
                align={config.legend.align || 'center'}
                layout={config.legend.layout || 'horizontal'}
                iconType={config.legend.iconType || 'line'}
                wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
              />
            )}

            {config.series.map((series, index) => {
              const strokeColor = series.stroke || config.colors?.[index % (config.colors?.length || 1)] || COLORS[index % COLORS.length];

              return (
                <Line
                  key={series.dataKey}
                  type={(series.curve as any) || 'monotone'}
                  dataKey={series.dataKey}
                  name={series.name}
                  stroke={strokeColor}
                  strokeWidth={2}
                  dot={series.dot === false ? false : series.dot || { r: 4 }}
                  activeDot={series.activeDot === false ? false : series.activeDot || { r: 6, strokeWidth: 1 }}
                  isAnimationActive={series.isAnimationActive !== false}
                  yAxisId={Array.isArray(config.yAxis) ? series.yAxisId || 0 : undefined}
                />
              );
            })}
          </LineChart>
        );

      case 'bar':
        return (
          <BarChart data={data}>
            {config.grid !== false && <CartesianGrid strokeDasharray="3 3" />}

            {config.xAxis && <XAxis dataKey={config.xAxis.dataKey} type={(config.xAxis.type === 'auto' ? 'category' : config.xAxis.type) as 'category' | 'number' | undefined} hide={config.xAxis.hide} />}

            {config.yAxis &&
              (Array.isArray(config.yAxis) ? (
                config.yAxis.map((axis, index) => (
                  <YAxis
                    key={index}
                    yAxisId={index}
                    dataKey={axis.dataKey}
                    label={axis.label ? { value: axis.label, angle: -90, position: 'insideLeft' } : undefined}
                    domain={axis.domain || ['auto', 'auto']}
                    orientation={index === 0 ? 'left' : 'right'}
                    hide={axis.hide}
                  />
                ))
              ) : (
                <YAxis
                  dataKey={config.yAxis.dataKey}
                  label={config.yAxis.label ? { value: config.yAxis.label, angle: -90, position: 'insideLeft' } : undefined}
                  domain={config.yAxis.domain || ['auto', 'auto']}
                  hide={config.yAxis.hide}
                />
              ))}

            <Tooltip
              contentStyle={{
                fontSize: '12px',
                backgroundColor: '#333', // Dark background
                color: '#fff', // Light text
                border: 'none',
                borderRadius: '4px',
              }}
            />

            {config.legend && <Legend verticalAlign={config.legend.verticalAlign || 'bottom'} align={config.legend.align || 'center'} layout={config.legend.layout || 'horizontal'} />}

            {config.series.map((series, index) => (
              <Bar
                key={series.dataKey}
                dataKey={series.dataKey}
                name={series.name}
                fill={series.fill || config.colors?.[index % (config.colors?.length || 1)] || COLORS[index % COLORS.length]}
                stackId={config.stacked ? 'a' : series.stackId}
                yAxisId={Array.isArray(config.yAxis) ? series.yAxisId || 0 : undefined}
              />
            ))}
          </BarChart>
        );

      case 'stackedBar':
        return (
          <BarChart data={data}>
            {config.grid !== false && <CartesianGrid strokeDasharray="3 3" />}

            {config.xAxis && (
              <XAxis dataKey={config.xAxis.dataKey} type={(config.xAxis.type === 'auto' ? 'category' : config.xAxis.type) as 'category' | 'number' | undefined} hide={config.xAxis.hide} tick={{ fontSize: 12 }} tickMargin={8} />
            )}

            {config.yAxis &&
              (Array.isArray(config.yAxis) ? (
                config.yAxis.map((axis, index) => (
                  <YAxis
                    key={index}
                    yAxisId={index}
                    dataKey={axis.dataKey}
                    label={axis.label ? { value: axis.label, angle: -90, position: 'insideLeft' } : undefined}
                    domain={axis.domain || ['auto', 'auto']}
                    orientation={index === 0 ? 'left' : 'right'}
                    hide={axis.hide}
                    tickFormatter={(value) => {
                      // Use custom formatter if specified in config
                      if (config.yAxis?.tickFormatter === 'formatCurrencyShortTooltip' && typeof value === 'number') {
                        return `$${value >= 1000 ? `${Math.round(value / 1000)}k` : value}`;
                      }

                      if (config.yAxis?.tickFormatter === 'formatCurrencyTooltip') {
                        return formatCurrencyTooltip(value, null);
                      }

                      if (config.yAxis?.tickFormatter === 'formatPercentTooltip') {
                        return formatPercentTooltip(value, null);
                      }

                      //formatPercentNormalized
                      // TODO: implement normalized percent formatting
                      // if (config.yAxis?.tickFormatter === 'formatPercentNormalized') {
                      //   // Sum all values for this tick across all series
                      //   const dataPoint = data.find((d) => d[axis.dataKey] === value);
                      //   if (!dataPoint) return `${value}%`;

                      //   console.log('datapoint', dataPoint);

                      //   const total = config.series.reduce((sum, series) => {
                      //     return sum + (dataPoint[series.dataKey] || 0);
                      //   }, 0);

                      //   const normalizedPct = total > 0 ? ((value / total) * 100).toFixed(2) : '0.00';
                      //   return formatPercentTooltip(normalizedPct, null);
                      // }

                      return value;
                    }}
                    tick={{ fontSize: 12 }}
                  />
                ))
              ) : (
                <YAxis
                  dataKey={config.yAxis.dataKey}
                  label={config.yAxis.label ? { value: config.yAxis.label, angle: -90, position: 'insideLeft' } : undefined}
                  domain={config.yAxis.domain || ['auto', 'auto']}
                  hide={config.yAxis.hide}
                  // tickFormatter={(value) => (typeof value === 'number' ? `$${value >= 1000 ? `${Math.round(value / 1000)}k` : value}` : value)}
                  tickFormatter={(value) => {
                    // Use custom formatter if specified in config
                    if (config.yAxis?.tickFormatter === 'formatCurrencyShortTooltip' && typeof value === 'number') {
                      return `$${value >= 1000 ? `${Math.round(value / 1000)}k` : value}`;
                    }

                    if (config.yAxis?.tickFormatter === 'formatCurrencyTooltip') {
                      return formatCurrencyTooltip(value, null);
                    }

                    if (config.yAxis?.tickFormatter === 'formatPercentTooltip') {
                      return formatPercentTooltip(value, null);
                    }

                    return value;
                  }}
                  tick={{ fontSize: 12 }}
                />
              ))}

            <Tooltip
              cursor={false}
              formatter={(value, name, props) => {
                // Use custom formatter if specified in config
                if (config.tooltip?.formatter === 'formatCurrencyTooltip') {
                  return formatCurrencyTooltip(value, name);
                }

                if (config.tooltip?.formatter === 'formatPercentTooltip') {
                  return formatPercentTooltip(value, name);
                }

                return [value, name];
              }}
              contentStyle={{
                fontSize: '12px',
                backgroundColor: '#333', // Dark background
                color: '#fff', // Light text
                border: 'none',
                borderRadius: '4px',
              }}
              itemStyle={{ padding: '2px 0' }}
              separator=": "
            />

            {config.legend && (
              <Legend verticalAlign={config.legend.verticalAlign || 'bottom'} align={config.legend.align || 'center'} layout={config.legend.layout || 'horizontal'} wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
            )}

            {config.series.map((series, index) => {
              // Determine the position of this series in the stack
              const stackId = series.stackId || 'a'; // Default stack ID
              const seriesInSameStack = config.series.filter((s) => (s.stackId || 'a') === stackId);
              // const isFirst = seriesInSameStack[0].dataKey === series.dataKey;
              const isLast = seriesInSameStack[seriesInSameStack.length - 1].dataKey === series.dataKey;

              // Set radius based on position in stack
              // Only the top bar in the stack gets rounded top corners
              // Only the bottom bar in the stack gets rounded bottom corners
              const radius: number | [number, number, number, number] = [
                isLast ? 4 : 0, // top-left radius
                isLast ? 4 : 0, // top-right radius
                0, // bottom-right radius
                0, // bottom-left radius
              ];

              return (
                <Bar
                  key={series.dataKey}
                  dataKey={series.dataKey}
                  name={series.name}
                  fill={series.fill || config.colors?.[index % (config.colors?.length || 1)] || COLORS[index % COLORS.length]}
                  stackId={series.stackId || 'a'} // Force stacking by default
                  yAxisId={Array.isArray(config.yAxis) ? series.yAxisId || 0 : undefined}
                  radius={radius}
                />
              );
            })}
          </BarChart>
        );

      case 'area':
        return (
          <AreaChart data={data}>
            {config.grid !== false && <CartesianGrid strokeDasharray="3 3" />}

            {config.xAxis && <XAxis dataKey={config.xAxis.dataKey} type={(config.xAxis.type === 'auto' ? 'category' : config.xAxis.type) as 'category' | 'number' | undefined} hide={config.xAxis.hide} />}

            {config.yAxis &&
              (Array.isArray(config.yAxis) ? (
                config.yAxis.map((axis, index) => (
                  <YAxis
                    key={index}
                    yAxisId={index}
                    dataKey={axis.dataKey}
                    label={axis.label ? { value: axis.label, angle: -90, position: 'insideLeft' } : undefined}
                    domain={axis.domain || ['auto', 'auto']}
                    orientation={index === 0 ? 'left' : 'right'}
                    hide={axis.hide}
                  />
                ))
              ) : (
                <YAxis
                  dataKey={config.yAxis.dataKey}
                  label={config.yAxis.label ? { value: config.yAxis.label, angle: -90, position: 'insideLeft' } : undefined}
                  domain={config.yAxis.domain || ['auto', 'auto']}
                  hide={config.yAxis.hide}
                />
              ))}

            <Tooltip
              contentStyle={{
                fontSize: '12px',
                backgroundColor: '#333', // Dark background
                color: '#fff', // Light text
                border: 'none',
                borderRadius: '4px',
              }}
            />

            {config.legend && <Legend verticalAlign={config.legend.verticalAlign || 'bottom'} align={config.legend.align || 'center'} layout={config.legend.layout || 'horizontal'} />}

            {config.series.map((series, index) => (
              <Area
                key={series.dataKey}
                type="monotone"
                dataKey={series.dataKey}
                name={series.name}
                fill={series.fill || config.colors?.[index % (config.colors?.length || 1)] || COLORS[index % COLORS.length]}
                stroke={series.stroke || series.fill || config.colors?.[index % (config.colors?.length || 1)] || COLORS[index % COLORS.length]}
                stackId={config.stacked ? 'a' : series.stackId}
              />
            ))}
          </AreaChart>
        );

      case 'pie':
        return (
          <PieChart>
            {config.legend && <Legend verticalAlign={config.legend.verticalAlign || 'bottom'} align={config.legend.align || 'center'} layout={config.legend.layout || 'horizontal'} />}

            <Pie
              data={data}
              dataKey={config.series[0].dataKey}
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={80}
              label={({ name, value, dollarAmount, format, totalCount }) => {
                // If dollarAmount exists, use it in the label
                if (dollarAmount !== undefined) {
                  return `$${(dollarAmount / 1000).toFixed(0)}k`;
                }
                if (format !== undefined) {
                  if (format === 'currencyShort') {
                    return `$${(value / 1000).toFixed(0)}k`;
                  }
                  if (format === 'percentString') {
                    const formattedPct = formatString(value, '%');
                    return totalCount !== undefined ? `${formattedPct} (${totalCount})` : formattedPct;
                  }
                }

                return value;
              }}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color || config.colors?.[index % (config.colors?.length || 1)] || COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            {/* <Tooltip
              contentStyle={{
                fontSize: '12px',
                backgroundColor: '#333', // Dark background
                color: '#fff', // Light text
                border: 'none',
                borderRadius: '4px',
              }}
              formatter={(value, name, props) => {
                // Use custom formatter if specified in config
                if (config.tooltip?.formatter === 'formatCurrencyTooltip') {
                  return formatCurrencyTooltip(value, name);
                }

                if (config.tooltip?.formatter === 'formatPercentTooltip') {
                  return formatPercentTooltip(value, name);
                }

                return [value, name];
              }}
            /> */}
            <Tooltip content={<CustomTooltip formatter={config.tooltip?.formatter} />} />
          </PieChart>
        );

      case 'radar':
        return (
          <RadarChart cx="50%" cy="50%" outerRadius="80%" data={data}>
            <PolarGrid />
            <PolarAngleAxis dataKey="category" />
            <PolarRadiusAxis />

            <Tooltip
              contentStyle={{
                fontSize: '12px',
                backgroundColor: '#333', // Dark background
                color: '#fff', // Light text
                border: 'none',
                borderRadius: '4px',
              }}
            />

            {config.legend && <Legend verticalAlign={config.legend.verticalAlign || 'bottom'} align={config.legend.align || 'center'} layout={config.legend.layout || 'horizontal'} />}

            {config.series.map((series, index) => (
              <Radar
                key={series.dataKey}
                name={series.name}
                dataKey={series.dataKey}
                stroke={series.stroke || config.colors?.[index % (config.colors?.length || 1)] || COLORS[index % COLORS.length]}
                fill={series.fill || `${config.colors?.[index % (config.colors?.length || 1)] || COLORS[index % COLORS.length]}88`}
                fillOpacity={0.6}
              />
            ))}
          </RadarChart>
        );

      case 'scatter':
        return (
          <ScatterChart>
            {config.grid !== false && <CartesianGrid strokeDasharray="3 3" />}

            {config.xAxis && <XAxis dataKey={config.xAxis.dataKey} type={(config.xAxis.type === 'auto' ? 'number' : config.xAxis.type) as 'number' | 'category' | undefined} hide={config.xAxis.hide} />}

            {config.yAxis &&
              (Array.isArray(config.yAxis) ? (
                config.yAxis.map((axis, index) => (
                  <YAxis
                    key={index}
                    yAxisId={index}
                    dataKey={axis.dataKey}
                    label={axis.label ? { value: axis.label, angle: -90, position: 'insideLeft' } : undefined}
                    domain={axis.domain || ['auto', 'auto']}
                    orientation={index === 0 ? 'left' : 'right'}
                    hide={axis.hide}
                  />
                ))
              ) : (
                <YAxis
                  dataKey={config.yAxis.dataKey}
                  label={config.yAxis.label ? { value: config.yAxis.label, angle: -90, position: 'insideLeft' } : undefined}
                  domain={config.yAxis.domain || ['auto', 'auto']}
                  hide={config.yAxis.hide}
                />
              ))}

            <Tooltip
              cursor={{ strokeDasharray: '3 3' }}
              contentStyle={{
                fontSize: '12px',
                backgroundColor: '#333', // Dark background
                color: '#fff', // Light text
                border: 'none',
                borderRadius: '4px',
              }}
            />

            {config.legend && <Legend verticalAlign={config.legend.verticalAlign || 'bottom'} align={config.legend.align || 'center'} layout={config.legend.layout || 'horizontal'} />}

            {config.series.map((series, index) => (
              <Scatter key={series.dataKey} name={series.name} data={data} fill={series.fill || config.colors?.[index % (config.colors?.length || 1)] || COLORS[index % COLORS.length]} />
            ))}
          </ScatterChart>
        );

      case 'composed':
        return (
          <ComposedChart data={data}>
            {config.grid !== false && <CartesianGrid strokeDasharray="3 3" />}

            {config.xAxis && <XAxis dataKey={config.xAxis.dataKey} type={(config.xAxis.type === 'auto' ? 'number' : config.xAxis.type) as 'number' | 'category' | undefined} hide={config.xAxis.hide} />}

            {config.yAxis &&
              (Array.isArray(config.yAxis) ? (
                config.yAxis.map((axis, index) => (
                  <YAxis
                    key={index}
                    yAxisId={index}
                    dataKey={axis.dataKey}
                    label={axis.label ? { value: axis.label, angle: -90, position: 'insideLeft' } : undefined}
                    domain={axis.domain || ['auto', 'auto']}
                    orientation={index === 0 ? 'left' : 'right'}
                    hide={axis.hide}
                  />
                ))
              ) : (
                <YAxis
                  dataKey={config.yAxis.dataKey}
                  label={config.yAxis.label ? { value: config.yAxis.label, angle: -90, position: 'insideLeft' } : undefined}
                  domain={config.yAxis.domain || ['auto', 'auto']}
                  hide={config.yAxis.hide}
                />
              ))}

            <Tooltip
              contentStyle={{
                fontSize: '12px',
                backgroundColor: '#333', // Dark background
                color: '#fff', // Light text
                border: 'none',
                borderRadius: '4px',
              }}
            />

            {config.legend && <Legend verticalAlign={config.legend.verticalAlign || 'bottom'} align={config.legend.align || 'center'} layout={config.legend.layout || 'horizontal'} />}

            {config.series.map((series, index) => {
              const color = series.fill || series.stroke || config.colors?.[index % (config.colors?.length || 1)] || COLORS[index % COLORS.length];

              switch (series.type) {
                case 'line':
                  return <Line key={series.dataKey} type="monotone" dataKey={series.dataKey} name={series.name} stroke={color} yAxisId={Array.isArray(config.yAxis) ? series.yAxisId || 0 : undefined} />;
                case 'bar':
                  return <Bar key={series.dataKey} dataKey={series.dataKey} name={series.name} fill={color} stackId={series.stackId} yAxisId={Array.isArray(config.yAxis) ? series.yAxisId || 0 : undefined} />;
                case 'area':
                  return <Area key={series.dataKey} type="monotone" dataKey={series.dataKey} name={series.name} fill={color} stroke={color} yAxisId={Array.isArray(config.yAxis) ? series.yAxisId || 0 : undefined} />;
                default:
                  return null;
              }
            })}
          </ComposedChart>
        );

      case 'databox':
        return (
          <Group spacing="md" noWrap>
            <DataBox key={`databox-${config.dataKey}`} dataBox={data[0]} />
          </Group>
        );

      case 'reportCard': {
        const cardConfig = config.reportCard || (data?.[0] as ReportCardConfig | undefined);

        if (!cardConfig) {
          return <Text>No data available</Text>;
        }

        return <ReportCard config={cardConfig} />;
      }

      case 'placeholder':
        return (
          <Box
            sx={(theme) => ({
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              minHeight: 180,
              backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
              borderRadius: theme.radius.md,
              padding: theme.spacing.md,
              border: `1px dashed ${theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[4]}`,
            })}
          >
            <IconChartBar size={48} opacity={0.3} />
            <Text align="center" color="dimmed" mt="md">
              {config.placeholderMessage || 'Chart data will be available soon'}
            </Text>
          </Box>
        );

      default:
        return <Text>Unsupported chart type: {config.type}</Text>;
    }
  };

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Group position="apart" mb="md" noWrap spacing="lg">
        <div style={{ flexGrow: 1, minWidth: 0 }}>
          <Title order={4}>{config.title}</Title>
          {config.description && (
            <Text size="sm" color="dimmed">
              {config.description}
            </Text>
          )}
        </div>

        {/* Display data boxes if available */}
        {config.dataBoxes && config.dataBoxes.length > 0 && (
          <Group spacing="md" noWrap>
            {config.dataBoxes.map((dataBox, index) => (
              <DataBox key={`databox-${index}`} dataBox={dataBox} />
            ))}
          </Group>
        )}
      </Group>
      <ResponsiveContainer width="100%" height={config.height || 300}>
        {renderChart()}
      </ResponsiveContainer>
    </Card>
  );
};

// TODO - extract all internal tooltips to leverage this component
const CustomTooltip = ({ active, payload, formatter }: { active?: boolean; payload?: any[]; formatter?: string }) => {
  if (active && payload && payload.length) {
    const style = {
      padding: '8px',
      fontSize: '12px',
      backgroundColor: '#333', // Dark background
      color: '#fff', // Light text
      border: 'none',
      borderRadius: '4px',
    };

    let displayValue = payload[0].value;
    let displayName = payload[0].name;

    if (formatter === 'formatCurrencyTooltip') {
      [displayValue, displayName] = formatCurrencyTooltip(payload[0].value, payload[0].name);
    } else if (formatter === 'formatPercentTooltip') {
      [displayValue, displayName] = formatPercentTooltip(payload[0].value, payload[0].name);
    }

    return (
      <div style={style}>
        <p>{`${displayName} : ${displayValue}`}</p>
      </div>
    );
  }

  return null;
};

const ReportCard = ({ config }: { config: ReportCardConfig }) => {
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];
  return (
    <Paper
      p="lg"
      radius="md"
      shadow="xs"
      sx={(theme) => ({
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
        border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
      })}
    >
      <Group position="apart" align="flex-start" spacing="xs">
        <div>
          <Text size="sm" color="dimmed">
            {config.subtitle}
          </Text>
          <Title order={3} mt={4}>
            {config.title}
          </Title>
        </div>
        {config.tooltip && (
          <MantineTooltip label={config.tooltip} position="left" multiline width={240}>
            <IconInfoCircle size={18} style={{ opacity: 0.6 }} />
          </MantineTooltip>
        )}
      </Group>

      <PieChart width={210} height={120} style={{ margin: '0 auto' }}>
        <Pie data={config.data} dataKey={config.dataKey} nameKey={config.nameKey} outerRadius="80%" innerRadius="60%" isAnimationActive={false}>
          {config.data.map((entry, index) => (
            <Cell key={`cell-${entry.name}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
      </PieChart>

      {/* <Text size="xl" weight={700} color={config.color || 'inherit'} mt="md">
        {displayValue}
      </Text> */}
    </Paper>
  );
};

// Also need to update the DataBox component to work with DataBoxConfig directly
const DataBox = ({ dataBox }: { dataBox: DataBoxConfig }) => {
  return (
    <Paper
      p="md"
      radius="md"
      shadow="xs"
      sx={(theme) => ({
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
        border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
        minWidth: 150,
        maxWidth: 200,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      })}
    >
      <Group position="center" spacing="xs">
        <Text size="sm" color="dimmed" weight={600} transform="uppercase">
          {dataBox.label}
        </Text>
        {dataBox.tooltip && (
          <MantineTooltip label={dataBox.tooltip} position="top" multiline width={220}>
            <IconInfoCircle size={16} style={{ opacity: 0.5 }} />
          </MantineTooltip>
        )}
      </Group>
      <Text size="xl" weight={700} color={dataBox.color || 'inherit'} mt={4} align="center">
        {dataBox.value}
      </Text>
      {dataBox.descriptor && (
        <Text size="xs" color="dimmed" mt={2} align="center" style={{ fontStyle: 'italic' }}>
          {dataBox.descriptor}
        </Text>
      )}
    </Paper>
  );
};
